import {
  HStack,
  IconButton,
  VStack,
  Center,
  Box,
} from '@chakra-ui/react';
import {
  FaPlay,
  FaPause,
  FaStepForward,
  FaStepBackward,
  FaFastForward,
  FaFastBackward,
} from 'react-icons/fa';
import { useAudioContext } from '../context/AudioContext';
import { useSync } from '../features/sync/context/SyncContext';

interface EmbedTransportControlsProps {
  onPlayPause: () => void;
  onForward: (seconds?: number) => void;
  onRewind: (seconds?: number) => void;
  onRewindToLastStart: () => void;
  isPlaying: boolean;
  playerCount: number;
}

const EmbedTransportControls: React.FC<EmbedTransportControlsProps> = ({
  onPlayPause,
  onForward,
  onRewind,
  onRewindToLastStart,
  isPlaying,
  playerCount,
}) => {
  const { 
    audioElements, 
    selectedPlayer,
    lastStartPositions,
    setLastStartPositions,
    pauseAll,
  } = useAudioContext();

  const {
    syncPoints,
    hasJumpedToSyncPoints,
  } = useSync();

  // Helper function to update last start positions when seeking during playback
  const updateLastStartPositions = () => {
    if (isPlaying) {
      const newStartPositions = new Map<number, number>();
      audioElements.forEach((audio, playerNumber) => {
        newStartPositions.set(playerNumber, audio.currentTime);
      });
      setLastStartPositions(newStartPositions);
    }
  };

  // Maintain sync when anchors are active while preserving mobile smoothness
  const seekForward = (seconds: number = 3) => {
    if (hasJumpedToSyncPoints && syncPoints.size > 1) {
      const reference = audioElements.get(selectedPlayer);
      if (!reference) {
        console.log('[Sync Forward] No reference audio element found');
        return;
      }
      const referenceSyncPoint = syncPoints.get(selectedPlayer);
      if (referenceSyncPoint !== undefined) {
        // Capture reference time once at the start
        const referenceCurrentTime = reference.currentTime;
        // Calculate target time for reference player
        const referenceTargetTime = Math.min(referenceCurrentTime + seconds, reference.duration || Infinity);
        // Calculate the new offset from sync point
        const targetOffset = referenceTargetTime - referenceSyncPoint;
        const absoluteTargetForUnsynced = referenceTargetTime;

        console.log('[Sync Forward] Starting forward', {
          seconds,
          selectedPlayer,
          referenceCurrentTime: referenceCurrentTime.toFixed(3),
          referenceTargetTime: referenceTargetTime.toFixed(3),
          referenceSyncPoint: referenceSyncPoint.toFixed(3),
          targetOffset: targetOffset.toFixed(3),
          absoluteTargetForUnsynced: absoluteTargetForUnsynced.toFixed(3),
          syncPoints: Array.from(syncPoints.entries()).map(([p, t]) => ({ player: p, syncPoint: t.toFixed(3) })),
        });

        const beforeTimes = new Map<number, number>();
        audioElements.forEach((audio, playerNumber) => {
          beforeTimes.set(playerNumber, audio.currentTime);
        });

        audioElements.forEach((audio, playerNumber) => {
          const playerSyncPoint = syncPoints.get(playerNumber);
          if (playerSyncPoint !== undefined) {
            const t = Math.min(playerSyncPoint + targetOffset, audio.duration || Infinity);
            audio.currentTime = Math.max(0, t);
            console.log('[Sync Forward] Player', playerNumber, {
              before: beforeTimes.get(playerNumber)?.toFixed(3),
              after: t.toFixed(3),
              playerSyncPoint: playerSyncPoint.toFixed(3),
              calculatedTime: (playerSyncPoint + targetOffset).toFixed(3),
              duration: audio.duration?.toFixed(3),
            });
          } else {
            audio.currentTime = absoluteTargetForUnsynced;
            console.log('[Sync Forward] Unsynced player', playerNumber, {
              before: beforeTimes.get(playerNumber)?.toFixed(3),
              after: absoluteTargetForUnsynced.toFixed(3),
            });
          }
        });

        updateLastStartPositions();
        return;
      } else {
        console.log('[Sync Forward] Reference sync point not found for selected player', selectedPlayer);
      }
    }
    onForward(seconds);
  };

  const seekRewind = (seconds: number = 3) => {
    if (hasJumpedToSyncPoints && syncPoints.size > 1) {
      const reference = audioElements.get(selectedPlayer);
      if (!reference) {
        console.log('[Sync Rewind] No reference audio element found');
        return;
      }
      const referenceSyncPoint = syncPoints.get(selectedPlayer);
      if (referenceSyncPoint !== undefined) {
        // Capture reference time once at the start
        const referenceCurrentTime = reference.currentTime;
        // Calculate target time for reference player
        const referenceTargetTime = Math.max(0, referenceCurrentTime - seconds);
        // Calculate the new offset from sync point
        const targetOffset = referenceTargetTime - referenceSyncPoint;
        const absoluteTargetForUnsynced = referenceTargetTime;

        console.log('[Sync Rewind] Starting rewind', {
          seconds,
          selectedPlayer,
          referenceCurrentTime: referenceCurrentTime.toFixed(3),
          referenceTargetTime: referenceTargetTime.toFixed(3),
          referenceSyncPoint: referenceSyncPoint.toFixed(3),
          targetOffset: targetOffset.toFixed(3),
          absoluteTargetForUnsynced: absoluteTargetForUnsynced.toFixed(3),
          syncPoints: Array.from(syncPoints.entries()).map(([p, t]) => ({ player: p, syncPoint: t.toFixed(3) })),
        });

        const beforeTimes = new Map<number, number>();
        audioElements.forEach((audio, playerNumber) => {
          beforeTimes.set(playerNumber, audio.currentTime);
        });

        audioElements.forEach((audio, playerNumber) => {
          const playerSyncPoint = syncPoints.get(playerNumber);
          if (playerSyncPoint !== undefined) {
            const t = Math.max(0, Math.min(playerSyncPoint + targetOffset, audio.duration || Infinity));
            audio.currentTime = t;
            console.log('[Sync Rewind] Player', playerNumber, {
              before: beforeTimes.get(playerNumber)?.toFixed(3),
              after: t.toFixed(3),
              playerSyncPoint: playerSyncPoint.toFixed(3),
              calculatedTime: (playerSyncPoint + targetOffset).toFixed(3),
              duration: audio.duration?.toFixed(3),
            });
          } else {
            audio.currentTime = absoluteTargetForUnsynced;
            console.log('[Sync Rewind] Unsynced player', playerNumber, {
              before: beforeTimes.get(playerNumber)?.toFixed(3),
              after: absoluteTargetForUnsynced.toFixed(3),
            });
          }
        });

        updateLastStartPositions();
        return;
      } else {
        console.log('[Sync Rewind] Reference sync point not found for selected player', selectedPlayer);
      }
    }
    onRewind(seconds);
  };

  const rewindToLastStartSyncAware = () => {
    if (hasJumpedToSyncPoints && syncPoints.size > 1) {
      const reference = audioElements.get(selectedPlayer);
      if (!reference) return;
      const referenceSyncPoint = syncPoints.get(selectedPlayer);
      if (referenceSyncPoint !== undefined) {
        const referenceStartPosition = lastStartPositions.get(selectedPlayer);
        if (referenceStartPosition === undefined) {
          return;
        }
        
        const startOffset = referenceStartPosition - referenceSyncPoint;
        
        audioElements.forEach((audio, playerNumber) => {
          const playerSyncPoint = syncPoints.get(playerNumber);
          if (playerSyncPoint !== undefined) {
            const playerStartPosition = lastStartPositions.get(playerNumber);
            if (playerStartPosition !== undefined) {
              const t = Math.max(0, Math.min(playerStartPosition, audio.duration || Infinity));
              audio.currentTime = t;
            } else {
              const t = Math.max(0, Math.min(playerSyncPoint + startOffset, audio.duration || Infinity));
              audio.currentTime = t;
            }
          } else {
            const playerStartPosition = lastStartPositions.get(playerNumber);
            if (playerStartPosition !== undefined) {
              const t = Math.max(0, Math.min(playerStartPosition, audio.duration || Infinity));
              audio.currentTime = t;
            } else {
              audio.currentTime = referenceStartPosition;
            }
          }
        });

        updateLastStartPositions();
        return;
      }
    }
    onRewindToLastStart();
  };

  return (
    <Box position="relative">
      <Center data-testid="embed-transport-controls">
        <VStack spacing={0}>
          <HStack spacing={4} justify="center">
            <IconButton
              data-testid="rewind-10-button"
              aria-label="Rewind 10 seconds"
              icon={<FaFastBackward />}
              onClick={() => seekRewind(10)}
              variant="ghost"
              size="lg"
              color="gray.400"
              _hover={{ 
                color: "accent.400",
                transform: 'scale(1.05)',
                borderColor: 'transparent'
              }}
              _active={{ 
                color: "accent.500",
                transform: 'scale(0.95)',
                borderColor: 'transparent'
              }}
            />
            <IconButton
              data-testid="rewind-3-button"
              aria-label="Rewind 3 seconds"
              icon={<FaStepBackward />}
              onClick={() => seekRewind(3)}
              variant="ghost"
              size="lg"
              color="gray.400"
              _hover={{ 
                color: "accent.400",
                transform: 'scale(1.05)',
                borderColor: 'transparent'
              }}
              _active={{ 
                color: "accent.500",
                transform: 'scale(0.95)',
                borderColor: 'transparent'
              }}
            />
              <IconButton
                data-testid="play-pause-button"
                aria-label="Play/Pause"
                icon={isPlaying ? <FaPause /> : <FaPlay />}
                onClick={() => {
                  if (isPlaying) {
                    pauseAll();
                  } else {
                    onPlayPause();
                  }
                }}
              size="lg"
              variant="ghost"
              color="gray.400"
              _hover={{ 
                color: "accent.400",
                transform: 'scale(1.05)',
                borderColor: 'transparent'
              }}
              _active={{ 
                color: "accent.500",
                transform: 'scale(0.95)',
                borderColor: 'transparent'
              }}
            />
            <IconButton
              data-testid="forward-3-button"
              aria-label="Forward 3 seconds"
              icon={<FaStepForward />}
              onClick={() => seekForward(3)}
              variant="ghost"
              size="lg"
              color="gray.400"
              _hover={{ 
                color: "accent.400",
                transform: 'scale(1.05)',
                borderColor: 'transparent'
              }}
              _active={{ 
                color: "accent.500",
                transform: 'scale(0.95)',
                borderColor: 'transparent'
              }}
            />
            <IconButton
              data-testid="forward-10-button"
              aria-label="Forward 10 seconds"
              icon={<FaFastForward />}
              onClick={() => seekForward(10)}
              variant="ghost"
              size="lg"
              color="gray.400"
              _hover={{ 
                color: "accent.400",
                transform: 'scale(1.05)',
                borderColor: 'transparent'
              }}
              _active={{ 
                color: "accent.500",
                transform: 'scale(0.95)',
                borderColor: 'transparent'
              }}
            />
          </HStack>
        </VStack>
      </Center>
    </Box>
  );
};

export default EmbedTransportControls;

